import { ColorEnum, PullbackScreenStatus } from '../graphqlTypes'
import {
   buildApprovableGroups,
   buildBlockedGroups,
   ChangeLogStep,
   HAPPY_AFTER_APPROVE,
   mockError,
   MockRecord,
   PULLBACK_SCENARIOS,
   PullbackScenario
} from './pmuPullbackMockScenarios'

// Stateful resolvers for the Application Pullback mock operations. Scenarios
// live in pmuPullbackMockScenarios.ts.

export const PULLBACK_OPERATION_NAMES = [
   'GetApplicationManagementActions',
   'GetPullbackTransitions',
   'InitiatePullback',
   'GetApplicationChangeLog',
   'ApprovePullback',
   'RejectPullback',
   'GetApplicationManagementLogs'
] as const

export type PullbackOperationName = typeof PULLBACK_OPERATION_NAMES[number]

export const isPullbackOperation = (
   name: string
): name is PullbackOperationName =>
   (PULLBACK_OPERATION_NAMES as readonly string[]).includes(name)

// ─── Stateful run tracking ───────────────────────────────────────────────────

export class MockNetworkError extends Error {
   constructor() {
      super('Mock network failure')
      this.name = 'MockNetworkError'
   }
}

// Name check, not instanceof: extending Error breaks instanceof under ES5 targets.
export const isMockNetworkError = (error: unknown): boolean =>
   error instanceof Error && error.name === 'MockNetworkError'

interface PullbackRun {
   scenarioId: string
   applicationId: string
   reads: number
   readsSinceApprove: number
   isApproved: boolean
   isRejected: boolean
   isEnded: boolean
   startedAt: string
   forcedStep: ChangeLogStep | null
   networkFailuresLeft: number
}

const pullbackRuns = new Map<string, PullbackRun>()
let pullbackRunCounter = 0

const DEFAULT_SCENARIO_ID = 'mock-happy'

const ENDING_STATUSES: PullbackScreenStatus[] = [
   PullbackScreenStatus.Blocked,
   PullbackScreenStatus.Succeeded,
   PullbackScreenStatus.Rejected,
   PullbackScreenStatus.TimedOut,
   PullbackScreenStatus.Failed
]

const pad = (value: number): string => String(value).padStart(2, '0')

const formatMockTimestamp = (date: Date): string =>
   `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ` +
   `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`

const getScenario = (scenarioId: string): PullbackScenario =>
   PULLBACK_SCENARIOS[scenarioId] ?? PULLBACK_SCENARIOS[DEFAULT_SCENARIO_ID]

const buildRequestNotFound = (requestId: string): MockRecord =>
   mockError('PullbackRequestNotFound')({ requestId, applicationId: '' })

const resolveStep = (run: PullbackRun): ChangeLogStep => {
   const scenario = getScenario(run.scenarioId)
   if (run.isRejected) return { status: PullbackScreenStatus.Rejected }
   if (run.forcedStep) return run.forcedStep
   if (run.isApproved) {
      const steps = scenario.afterApprove ?? HAPPY_AFTER_APPROVE
      const step = steps[Math.min(run.readsSinceApprove, steps.length - 1)]
      run.readsSinceApprove += 1
      return step
   }
   if (scenario.isBlocked) {
      return { status: PullbackScreenStatus.Blocked, withList: true }
   }
   return { status: PullbackScreenStatus.AwaitingApproval, withList: true }
}

const getMockGroups = (
   step: ChangeLogStep,
   scenario: PullbackScenario
): MockRecord[] => {
   if (!step.withList) return []
   if (step.status === PullbackScreenStatus.Blocked) return buildBlockedGroups()
   if (scenario.isBlocked) return buildBlockedGroups()
   return buildApprovableGroups()
}

const buildChangeLog = (requestId: string, applicationId: string) => {
   const run = pullbackRuns.get(requestId)
   if (!run) {
      return {
         __typename: 'Query',
         getApplicationChangeLog: buildRequestNotFound(requestId)
      }
   }
   const scenario = getScenario(run.scenarioId)
   if (scenario.changeLogError && run.reads === 0) {
      run.reads += 1
      return {
         __typename: 'Query',
         getApplicationChangeLog: scenario.changeLogError({
            requestId,
            applicationId
         })
      }
   }

   if (run.isApproved && run.networkFailuresLeft > 0) {
      run.networkFailuresLeft -= 1
      throw new MockNetworkError()
   }

   run.reads += 1
   const step = resolveStep(run)
   if (ENDING_STATUSES.includes(step.status)) run.isEnded = true
   const moduleGroups = getMockGroups(step, scenario)

   return {
      __typename: 'Query',
      getApplicationChangeLog: {
         __typename: 'GetApplicationChangeLogResult',
         requestId,
         status: step.status,
         failureReason: step.failureReason ?? null,
         changeLogToken:
            step.status === PullbackScreenStatus.AwaitingApproval
               ? `mock-token-${run.reads}`
               : null,
         moduleGroups
      }
   }
}

const buildDecision = (
   kind: 'approvePullback' | 'rejectPullback',
   requestId: string,
   applicationId: string
): MockRecord => {
   const run = pullbackRuns.get(requestId)
   if (!run) {
      return { __typename: 'Mutation', [kind]: buildRequestNotFound(requestId) }
   }
   const scenario = getScenario(run.scenarioId)
   const error =
      kind === 'approvePullback' ? scenario.approveError : scenario.rejectError
   if (error) {
      run.forcedStep = scenario.statusAfterDecisionError ?? null
      return {
         __typename: 'Mutation',
         [kind]: error({ requestId, applicationId })
      }
   }

   if (kind === 'rejectPullback') {
      run.isRejected = true
      run.isEnded = true
      return {
         __typename: 'Mutation',
         rejectPullback: {
            __typename: 'RejectPullbackResult',
            requestId,
            status: PullbackScreenStatus.Rejected
         }
      }
   }

   run.isApproved = true
   run.readsSinceApprove = 0
   run.networkFailuresLeft = scenario.networkFailuresAfterApprove ?? 0
   return {
      __typename: 'Mutation',
      approvePullback: {
         __typename: 'ApprovePullbackResult',
         requestId,
         status: scenario.approveStatus ?? PullbackScreenStatus.Running
      }
   }
}

const buildInitiate = (
   toStageId: string,
   applicationId: string
): MockRecord => {
   const scenario = getScenario(toStageId)
   if (scenario.initiateError) {
      return {
         __typename: 'Mutation',
         initiatePullback: scenario.initiateError({
            requestId: '',
            applicationId
         })
      }
   }

   pullbackRunCounter += 1
   const requestId = `mock-pullback-req-${pullbackRunCounter}`
   pullbackRuns.set(requestId, {
      scenarioId: PULLBACK_SCENARIOS[toStageId]
         ? toStageId
         : DEFAULT_SCENARIO_ID,
      applicationId,
      reads: 0,
      readsSinceApprove: 0,
      isApproved: false,
      isRejected: false,
      isEnded: false,
      startedAt: formatMockTimestamp(new Date()),
      forcedStep: null,
      networkFailuresLeft: 0
   })
   return {
      __typename: 'Mutation',
      initiatePullback: { __typename: 'InitiatePullbackResult', requestId }
   }
}

// An application id starting with this prefix gets no currentStage (AC9.2).
const NO_CURRENT_STAGE_APPLICATION_PREFIX = 'mock-no-current-stage'

const buildTransitions = (applicationId: string): MockRecord => ({
   __typename: 'Query',
   getPullbackTransitions: {
      __typename: 'GetPullbackTransitionsResult',
      currentStage: applicationId.startsWith(
         NO_CURRENT_STAGE_APPLICATION_PREFIX
      )
         ? null
         : {
              __typename: 'PullbackCurrentStage',
              stageId: 'mock-current',
              stageName: 'Mock: current stage',
              stageColor: ColorEnum.Blue
           },
      transitions: Object.entries(PULLBACK_SCENARIOS).map(
         ([stageId, scenario]) => ({
            __typename: 'PullbackTransition',
            stageId,
            stageName: scenario.label,
            stageColor: scenario.color ?? null,
            leftStageAt: '2026-09-18 09:00:00'
         })
      )
   }
})

// Pullback log rows for the open run of an application (what the real
// getApplicationManagementLogs returns while a run is INITIATED / IN_PROGRESS).
// One COMPLETED login-number change so the detail card can be viewed in dev.
const buildLoginPhoneUpdateLog = (applicationId: string): MockRecord => ({
   __typename: 'ApplicationLoginPhoneNumberUpdateLog',
   id: 'mock-login-phone-log-001',
   pipelineItemId: applicationId,
   applicationNo: 'MOCK/2026/0001',
   createdAt: '2026-10-07T09:30:00.000Z',
   completedAt: '2026-10-07T09:30:02.000Z',
   doneBy: {
      __typename: 'BaseUser',
      userId: 'mock-user-001',
      name: 'Mock Officer',
      username: 'mock.officer'
   },
   actionType: 'UPDATE_APPLICATION_LOGIN_PHONE_NUMBER',
   remarks: 'Mock reason',
   attachments: [],
   status: 'COMPLETED',
   errorMessage: null,
   fieldDiffs: [
      {
         __typename: 'PMUFieldDiffType',
         fieldId: 'mock-phone-field',
         fieldRefId: 'mock-phone-field-ref',
         oldValue: '+91 9876543210',
         newValue: '+91 9123456789',
         fieldLabel: 'Login phone number',
         gofName: null
      }
   ]
})

const buildOpenPullbackLogs = (applicationId: string): MockRecord => ({
   __typename: 'Query',
   getApplicationManagementLogs: {
      __typename: 'GetApplicationManagementLogsResult',
      totalCount: 0,
      logs: [
         ...Array.from(pullbackRuns.entries())
            .filter(
               ([, run]) => run.applicationId === applicationId && !run.isEnded
            )
            .map(([requestId, run]) => ({
               __typename: 'ApplicationManagementPullback',
               id: requestId,
               pipelineItemId: applicationId,
               applicationNo: 'MOCK/2026/0001',
               createdAt: run.startedAt,
               doneBy: {
                  __typename: 'BaseUser',
                  userId: 'mock-user-001',
                  name: 'Mock Officer',
                  username: 'mock.officer'
               },
               actionType: 'PULL_BACK_APPLICATION',
               remarks: 'Mock reason',
               attachments: [],
               status: run.isApproved ? 'IN_PROGRESS' : 'INITIATED',
               failureReason: null,
               changeConsents: []
            })),
         buildLoginPhoneUpdateLog(applicationId)
      ]
   }
})

interface PullbackParams {
   requestId?: string
   applicationId?: string
   pipelineItemId?: string
   toStageId?: string
}

export const buildPullbackData = (
   operationName: PullbackOperationName,
   variables: Record<string, unknown>
): Record<string, unknown> => {
   const params = (variables?.['params'] ?? {}) as PullbackParams
   const requestId = params.requestId ?? ''
   const applicationId = params.applicationId ?? 'mock-app-001'

   switch (operationName) {
      case 'GetApplicationManagementActions':
         return {
            __typename: 'Query',
            getApplicationManagementActions: {
               __typename: 'GetApplicationManagementActionsResult',
               actions: [
                  {
                     __typename: 'ApplicationManagementActionAvailability',
                     actionType: 'PULL_BACK_APPLICATION',
                     label: 'Pull Back Application',
                     isAllowed: true,
                     blockedReason: null
                  }
               ]
            }
         }
      case 'GetPullbackTransitions':
         return buildTransitions(applicationId)
      case 'InitiatePullback':
         return buildInitiate(params.toStageId ?? '', applicationId)
      case 'GetApplicationChangeLog':
         return buildChangeLog(requestId, applicationId)
      case 'ApprovePullback':
         return buildDecision('approvePullback', requestId, applicationId)
      case 'RejectPullback':
         return buildDecision('rejectPullback', requestId, applicationId)
      case 'GetApplicationManagementLogs':
         return buildOpenPullbackLogs(params.pipelineItemId ?? '')
      default:
         return {}
   }
}
